import { Injectable } from '@angular/core';
import { MachineApiService } from './machine-api.service';

type ApiResponse<T> = { data: T; success: boolean };

export type IntegrationProvider = 'google-genai' | 'openai' | 'anthropic' | 'xai';
export type IntegrationGitHubInstallationMode = 'business' | 'personal';

export type IntegrationGithubInstallationRef = {
  installationId?: number;
  accountLogin?: string;
  accountType?: 'User' | 'Organization' | 'Unknown';
  connected: boolean;
};

export type IntegrationProviderStatus = {
  provider: IntegrationProvider;
  connected: boolean;
  maskedApiKey?: string;
  updatedAt?: number;
};

export type IntegrationPreferences = {
  defaultProvider?: IntegrationProvider;
  defaultModel?: string;
  routeModelDefaultChat?: string;
  routeModelDefaultTickets?: string;
  surfaceModelDefaults?: Record<string, string>;
};

export type IntegrationSnapshot = {
  providers: IntegrationProviderStatus[];
  preferences: IntegrationPreferences;
  github: {
    business: IntegrationGithubInstallationRef;
    personal: IntegrationGithubInstallationRef;
    personals: Array<IntegrationGithubInstallationRef & { active: boolean }>;
    activePersonalInstallationId?: number;
    activeInstallationMode: IntegrationGitHubInstallationMode;
    resolvedInstallationId: number | null;
    connected: boolean;
  };
  lastUpdatedAt: number | null;
};

@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  constructor(private api: MachineApiService) {}

  private async unwrap<T>(promise: Promise<ApiResponse<T>>): Promise<T> {
    const res = await promise;
    return res.data;
  }

  async getSnapshot(): Promise<IntegrationSnapshot> {
    return this.unwrap(this.api.request<ApiResponse<IntegrationSnapshot>>('/api/integrations'));
  }

  async setProviderApiKey(provider: IntegrationProvider, apiKey: string): Promise<IntegrationSnapshot> {
    return this.unwrap(
      this.api.request<ApiResponse<IntegrationSnapshot>>(`/api/integrations/providers/${encodeURIComponent(provider)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey }),
      })
    );
  }

  async removeProviderApiKey(provider: IntegrationProvider): Promise<IntegrationSnapshot> {
    return this.unwrap(
      this.api.request<ApiResponse<IntegrationSnapshot>>(`/api/integrations/providers/${encodeURIComponent(provider)}`, {
        method: 'DELETE',
      })
    );
  }

  async updatePreferences(input: IntegrationPreferences): Promise<IntegrationSnapshot> {
    return this.unwrap(
      this.api.request<ApiResponse<IntegrationSnapshot>>('/api/integrations/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input ?? {}),
      })
    );
  }

  async updateGithubContext(input: {
    business?: { installationId?: number; accountLogin?: string; accountType?: 'User' | 'Organization' | 'Unknown' };
    personal?: { installationId?: number; accountLogin?: string; accountType?: 'User' | 'Organization' | 'Unknown' };
    personals?: Array<{ installationId?: number; accountLogin?: string; accountType?: 'User' | 'Organization' | 'Unknown' }>;
    activePersonalInstallationId?: number;
    activeInstallationMode?: IntegrationGitHubInstallationMode;
    installationMode?: IntegrationGitHubInstallationMode;
    installationId?: number;
    accountLogin?: string;
  }): Promise<IntegrationSnapshot> {
    return this.unwrap(
      this.api.request<ApiResponse<IntegrationSnapshot>>('/api/integrations/github/context', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input ?? {}),
      })
    );
  }

  async clearGithubContext(): Promise<IntegrationSnapshot> {
    return this.unwrap(
      this.api.request<ApiResponse<IntegrationSnapshot>>('/api/integrations/github/context', {
        method: 'DELETE',
      })
    );
  }
}
